"use client"
import { useTranslations } from "next-intl"

import { useEffect, useState, type ReactNode } from "react"

import { useRecentActivity } from "@/context/UserContext"

import { CardCarousel } from "@/components/layout/CardCarousel"
import { Fade } from "@/components/ui/motion"
import type { ActivityItem } from "@/lib/activity/types"
import { capitalize } from "@/lib/utils"

/**
 * The strip along the foot of the landing canvas, for a reader who has been here before.
 *
 * The exact inverse of FeatureStrip, in the same place and under the same choreography. That
 * strip explains the product to someone who cannot use it yet; this one hands back the work of
 * someone who can, and its own comment gives the reason both cannot be right at once: a
 * subscriber opening the app for the two-hundredth time does not need the product advertised to
 * them. The canvas renders whichever the reader's entitlement calls for, never both.
 *
 * Not under the composer, which is the other obvious place. At rest the landing screen is a
 * greeting and a rule and nothing else — the examples are latent until the box is engaged — and a
 * list under the rule turns a page with one instruction into a page with two, then argues with
 * the examples panel when it opens.
 */

/**
 * How long ago, in the coarsest unit that is still true.
 *
 * In the browser rather than on the server because "yesterday" is a fact about the reader's
 * clock, not the database's — and lib/utils' formatDate answers a different question (the date a
 * judgment was handed down, where the exact day is the point).
 */
function timeAgo(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ""

  const minutes = Math.round((Date.now() - then) / 60000)
  if (minutes < 1) return "just now"
  if (minutes < 60) return `${minutes}m ago`

  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`

  const days = Math.round(hours / 24)
  if (days === 1) return "yesterday"
  if (days < 30) return `${days}d ago`

  const months = Math.round(days / 30)
  return months < 12 ? `${months}mo ago` : `${Math.round(months / 12)}y ago`
}

function ActivityCard({ item }: { item: ActivityItem }) {
  return (
    <div className="flex flex-col gap-3">
      {/*
        The label above the thing, unlike FeatureCard, because the thing is a sentence someone
        wrote and the label is furniture. Reading "Research answer" first is what tells you the
        question underneath is one of yours rather than a suggestion.
      */}
      <p className="text-sm font-normal leading-none text-muted-foreground">
        {[capitalize(item.subtitle || ""), timeAgo(item.at)].filter(Boolean).join(" • ")}
      </p>
      {/*
        Clamped, not truncated to one line: a research question is often a full sentence, and
        the first eight words of one are frequently the same eight words as another's.
      */}
      <p className="line-clamp-3 text-base font-medium leading-snug text-foreground">
        {item.title}
      </p>
    </div>
  )
}

export function RecentActivity({
  emptyState,
  loadingState,
}: { emptyState?: ReactNode; loadingState?: ReactNode } = {}) {
  const t = useTranslations("research.RecentActivity")
  /*
    From the user store, which fetched it once the app settled and keeps it (see
    context/UserContext) — so a reader opening their history sees it at once rather than after a
    request made on opening. Mounting this refreshes it if it has gone stale, in place.

    `null` while it is unknown, `[]` for a reader with no history. The difference is the whole
    behaviour of this component: it draws nothing in either case, so a first visit never flashes
    a skeleton into the foot of a landing page and then takes it away again.
  */
  const items = useRecentActivity()

  /*
    Held at zero for one frame after the items land, so there is something to fade FROM.

    `Fade` mounts with `initial={false}` — deliberately, so a surface that should start hidden is
    drawn hidden rather than fading out in front of the reader — which means mounting it already
    `show` produces no animation at all. The strip has to render once at `show={false}` and turn
    on afterwards. A frame, not a timeout: the point is to be one paint late, not a measured
    delay.

    The band around this already has a `Reveal` and a `Fade`, and neither can do this job. Both
    run when the CANVAS mounts, which is well before `/api/activity` answers — so the foot
    revealed itself empty and the carousel appeared in it a few hundred milliseconds later,
    fully formed. This is the arrival of the DATA, which is a different event.
  */
  const [shown, setShown] = useState(false)

  useEffect(() => {
    if (!items || items.length === 0) return
    const frame = requestAnimationFrame(() => setShown(true))
    return () => cancelAnimationFrame(frame)
  }, [items])

  /*
    Two different nothings, and only one of them has anything to say.

    `null` is "not known yet" — the fetch is in flight — and must stay silent wherever this is
    used: on the landing page a message here would flash into the foot of the page and be taken
    away again on a first visit. `[]` is "asked, and there is no history", which in a drawer
    opened ON PURPOSE deserves a sentence rather than an empty box.

    `loadingState` is the same distinction from the other side. On the landing page there is
    nothing to hold open, so it stays absent and the strip simply appears when it is ready. In a
    drawer the height is the reader's problem: without it the sheet opens as a thin strip holding
    only its title and then jumps taller when the fetch lands.

    Both absent by default, so the landing page behaves exactly as it did: draw nothing, either
    way.
  */
  if (!items) return loadingState ?? null
  if (items.length === 0) return emptyState ?? null

  return (
    // Matches FeatureStrip's gutter for the reason its comment gives: the carousel measures this
    // padding to break its track out to the full viewport while keeping the first card flush.
    // `Fade` renders that same single padded element, so the measurement is unchanged — it only
    // animates opacity, and never a transform that would move what the carousel is measuring.
    <Fade show={shown} className="w-full px-6">
      <CardCarousel
        // title={t("pickUpWhereYouLeft")}
        cards={items.map((item) => (
          <ActivityCard key={item.key} item={item} />
        ))}
        // Parallel to `cards`, so a card navigates to the thing it names.
        cardHrefs={items.map((item) => item.href)}
        // Every dimension matched to FeatureStrip, so the foot of the canvas has one silhouette
        // whichever strip is standing in it, and stated explicitly for the same reason: these are
        // shared defaults that another page is entitled to change.
        cardMaxWidth={400}
        cardMinHeight={124}
        cardGap={12}
        // Every card here goes somewhere — `cardHrefs` above is parallel and complete — so the
        // strip says so on hover. FeatureStrip, which stands in the same place for a reader who
        // cannot research yet, is explanatory rather than navigational and does not.
        cardHoverFill
        showDots
        showArrows
      />
    </Fade>
  )
}
